import React, { useEffect, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import {
  fetchAdminBusinessProfile,
  fetchAdminBusinessProfiles,
  reviewAdminBusinessProfile,
} from '../services/api/admin';
import type { AdminBusinessProfile } from '../types/admin';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATUS_OPTIONS = ['SUBMITTED', 'DRAFT', 'APPROVED', 'REJECTED', 'SUSPENDED'];

function formatDate(value?: string | null): string {
  if (!value) return 'Not supplied';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

const AdminBusinessProfilesPage: React.FC = () => {
  const [profiles, setProfiles] = useState<AdminBusinessProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<AdminBusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    query: '',
    status: 'SUBMITTED',
  });

  const loadProfiles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchAdminBusinessProfiles({
        ...(filters.query ? { query: filters.query.trim() } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        page,
        size: 12,
        sort: 'submittedAt,desc',
      });
      setProfiles(response.items);
      setTotalPages(response.totalPages);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load business profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const openProfile = async (profileId: string) => {
    setDetailLoading(true);
    setError('');
    setSuccess('');
    try {
      const detail = await fetchAdminBusinessProfile(profileId);
      setSelectedProfile(detail);
      setReviewNote(detail.verificationNote || '');
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load business profile details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReview = async (approved: boolean) => {
    if (!selectedProfile || actionLoading) return;
    if (!approved && !reviewNote.trim()) {
      setError('Add a rejection note before rejecting this business profile.');
      return;
    }

    setActionLoading(approved ? 'approve' : 'reject');
    setError('');
    setSuccess('');
    try {
      const nextProfile = await reviewAdminBusinessProfile(selectedProfile.id, {
        approved,
        note: reviewNote.trim() || undefined,
      });
      setSelectedProfile(nextProfile);
      setSuccess(approved ? 'Business profile approved.' : 'Business profile rejected.');
      await loadProfiles();
    } catch (reviewError: any) {
      setError(reviewError.message || 'Could not review business profile.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <AdminShell
      badge="Business Customers"
      copy="Review submitted business customer profiles and approve or reject onboarding."
      title="Business profiles"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {success ? <div className="register-success-banner">{success}</div> : null}

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Review queue</h2>
            <p className="admin-panel-copy">Search submitted businesses by company, owner, email, or registration number.</p>
          </div>
        </div>

        <div className="admin-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder="Search business profiles"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, status: event.target.value }));
            }}
            value={filters.status}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table" style={{ marginTop: '16px' }}>
              <div className="admin-table-head admin-table-head-business-profiles">
                <span>Business</span>
                <span>Owner</span>
                <span>Status</span>
                <span>Submitted</span>
                <span>Action</span>
              </div>
              {profiles.map((profile) => (
                <button
                  className={`admin-table-row admin-table-row-button admin-table-row-business-profiles${
                    selectedProfile?.id === profile.id ? ' admin-table-row-selected' : ''
                  }`}
                  key={profile.id}
                  onClick={() => void openProfile(profile.id)}
                  type="button"
                >
                  <span>
                    <strong>{profile.tradingName || profile.legalName}</strong>
                    <small>{profile.legalName}</small>
                    <small>{profile.registrationNumber || 'No registration number'}</small>
                  </span>
                  <span>
                    <strong>{profile.ownerName || 'Owner unavailable'}</strong>
                    <small>{profile.ownerEmail || 'No owner email'}</small>
                  </span>
                  <span>
                    <strong>{profile.verificationStatus}</strong>
                    <small>{profile.verificationNote || 'No review note'}</small>
                  </span>
                  <span>
                    <strong>{formatDate(profile.submittedAt)}</strong>
                    <small>Branches: {profile.branches.length}</small>
                  </span>
                  <span>
                    <small>Open review</small>
                  </span>
                </button>
              ))}
              {!profiles.length ? <p className="admin-empty">No business profiles match the current filters.</p> : null}
            </div>
            <div className="admin-pagination">
              <button className="register-secondary-button" disabled={page === 0} onClick={() => setPage((current) => Math.max(current - 1, 0))} type="button">
                Previous
              </button>
              <span>
                Page {page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button className="register-secondary-button" disabled={page + 1 >= Math.max(totalPages, 1)} onClick={() => setPage((current) => current + 1)} type="button">
                Next
              </button>
            </div>
          </>
        )}
      </section>

      {selectedProfile ? (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">{selectedProfile.tradingName || selectedProfile.legalName}</h2>
              <p className="admin-panel-copy">Profile ID {selectedProfile.id}</p>
            </div>
            {detailLoading ? <div className="register-loader register-loader-small" /> : null}
          </div>

          <div className="admin-detail-grid paystack-subaccount-grid">
            <div className="admin-detail-card"><span>Legal name</span><strong>{selectedProfile.legalName}</strong></div>
            <div className="admin-detail-card"><span>Trading name</span><strong>{selectedProfile.tradingName || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Registration number</span><strong>{selectedProfile.registrationNumber || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>VAT number</span><strong>{selectedProfile.vatNumber || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Business email</span><strong>{selectedProfile.businessEmail || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Business phone</span><strong>{selectedProfile.businessPhone || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Representative</span><strong>{selectedProfile.representativeName || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Representative email</span><strong>{selectedProfile.representativeEmail || 'Not supplied'}</strong></div>
            <div className="admin-detail-card admin-detail-card-span-2"><span>Head office</span><strong>{selectedProfile.headOfficeAddress || 'Not supplied'}</strong></div>
            <div className="admin-detail-card"><span>Owner</span><strong>{selectedProfile.ownerName || selectedProfile.ownerUserId}</strong></div>
            <div className="admin-detail-card"><span>Owner email</span><strong>{selectedProfile.ownerEmail || 'Not supplied'}</strong></div>
          </div>

          <div className="admin-section-grid" style={{ marginTop: '18px' }}>
            <div className="admin-panel admin-panel-nested">
              <h3 className="admin-panel-title">Branches</h3>
              <div className="register-feature-list">
                {selectedProfile.branches.map((branch) => (
                  <div className="register-feature-item" key={branch.id}>
                    <span className="register-feature-icon">{branch.defaultBranch ? 'HQ' : 'BR'}</span>
                    <div>
                      <div className="register-feature-title">{branch.branchName}</div>
                      <div className="register-feature-copy">{branch.address}</div>
                      <div className="register-feature-copy">{branch.contactEmail || branch.contactPhone || 'No branch contact supplied'}</div>
                    </div>
                  </div>
                ))}
                {!selectedProfile.branches.length ? <p className="admin-empty">No branches supplied.</p> : null}
              </div>
            </div>

            <div className="admin-panel admin-panel-nested">
              <h3 className="admin-panel-title">Review</h3>
              <div className="register-field">
                <label className="register-label" htmlFor="businessReviewNote">
                  Review note
                </label>
                <textarea
                  className="register-input"
                  id="businessReviewNote"
                  onChange={(event) => setReviewNote(event.target.value)}
                  placeholder="Add approval context or rejection instructions"
                  rows={5}
                  value={reviewNote}
                />
              </div>
              <div className="register-flow-actions">
                <button className="register-submit register-submit-inline" disabled={Boolean(actionLoading)} onClick={() => void submitReview(true)} type="button">
                  {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                </button>
                <button className="register-secondary-button" disabled={Boolean(actionLoading)} onClick={() => void submitReview(false)} type="button">
                  {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </AdminShell>
  );
};

export default AdminBusinessProfilesPage;
